import React, { useCallback, useEffect, useMemo, useState } from 'react'
import BaseLayout from '../../admin/BaseLayout'
import { useAuth } from '../../contexts/AuthContext'
import { API_BASE_URL } from '../../config/api'
import './OracleCRM.css'

const CRM_SESSION_KEY = 'filtermarket_oracle_crm_password'
const PERIODS = [['day', 'Hoje'], ['week', '7 dias'], ['month', 'Este mês'], ['all', 'Todo o histórico']]
const TABS = [['leads', 'Leads'], ['conversations', 'Conversas'], ['live', 'Oráculo Live'], ['polymarket', 'Polymarket'], ['quantumodds', 'Quantum Odds']]
const STAGES = [
  ['new', 'Nova lead'],
  ['contacted', 'Contactada'],
  ['qualified', 'Qualificada'],
  ['customer', 'Cliente'],
  ['not_fit', 'Sem fit'],
]
const PROFILE_FIELDS = [
  ['ageRange', 'Faixa etária'],
  ['financialSituation', 'Condição financeira / banca'],
  ['occupation', 'Profissão'],
  ['education', 'Educação'],
  ['location', 'Zona / localização'],
  ['bookmakers', 'Casas ou exchanges que utiliza'],
  ['goals', 'Desejos e objetivos'],
  ['pains', 'Dores e problemas a apostar'],
  ['mannerisms', 'Maneirismos e forma de comunicar'],
]

const emptyProfile = () => ({
  stage: 'new',
  notes: '',
  fields: PROFILE_FIELDS.reduce((fields, [key]) => ({ ...fields, [key]: '' }), {}),
})
const integer = (value) => new Intl.NumberFormat('pt-PT').format(Number(value) || 0)
const money = (value) => `$${Number(value || 0).toFixed(4)}`
const formatDate = (value) => value
  ? new Date(value).toLocaleString('pt-PT', { dateStyle: 'short', timeStyle: 'short' })
  : '—'
const sourceLabel = (source) => source === 'live' ? 'Oráculo Live'
  : source === 'polymarket' ? 'Oráculo Polymarket'
    : source === 'quantumodds' ? 'Oráculo Quantum Odds' : 'Oráculo'

const Tags = ({ items = [], tone = 'default', empty = 'Ainda sem dados' }) => (
  <div className="oracle-crm-tags">
    {items.length ? items.map((item) => <span className={`is-${tone}`} key={item}>{item}</span>) : <em>{empty}</em>}
  </div>
)

const Conversation = ({ turn, initiallyOpen = false }) => (
  <article className="oracle-crm-conversation">
    <div className="oracle-crm-conversation-meta">
      <span className={`oracle-crm-source is-${turn.source || 'oracle'}`}>{turn.sourceLabel || sourceLabel(turn.source)}</span>
      <span>{formatDate(turn.askedAt)}</span>
      {turn.eventId != null && <span>Evento {turn.eventId}</span>}
      <span className={`oracle-crm-status is-${turn.status || 'unknown'}`}>{turn.status || '—'}</span>
    </div>
    <h4>Pergunta</h4>
    <p className="oracle-crm-question">{turn.question || 'Sem pergunta guardada.'}</p>
    <details open={initiallyOpen}>
      <summary>Resposta completa</summary>
      <p className="oracle-crm-answer">{turn.answer || turn.errorMessage || 'Sem resposta guardada.'}</p>
    </details>
    <footer>
      <span>{turn.model || 'modelo não registado'}</span>
      <span>{integer(turn.usage?.totalTokens)} tokens</span>
      <span>{turn.cost?.known === false ? 'custo desconhecido' : money(turn.costUsd)}</span>
      <span>{(turn.tools || []).join(', ') || 'sem ferramentas'}</span>
    </footer>
  </article>
)

const OracleCRM = () => {
  const { user } = useAuth()
  const [password, setPassword] = useState(() => sessionStorage.getItem(CRM_SESSION_KEY) || '')
  const [authorized, setAuthorized] = useState(false)
  const [activeTab, setActiveTab] = useState('leads')
  const [period, setPeriod] = useState('all')
  const [overview, setOverview] = useState(null)
  const [leads, setLeads] = useState([])
  const [leadTotal, setLeadTotal] = useState(0)
  const [leadSearch, setLeadSearch] = useState('')
  const [activeLeadSearch, setActiveLeadSearch] = useState('')
  const [selectedLead, setSelectedLead] = useState(null)
  const [profileDraft, setProfileDraft] = useState(emptyProfile)
  const [conversations, setConversations] = useState([])
  const [conversationTotal, setConversationTotal] = useState(0)
  const [conversationOffset, setConversationOffset] = useState(0)
  const [conversationSearch, setConversationSearch] = useState('')
  const [activeConversationSearch, setActiveConversationSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const adminRequest = useCallback(async (path, options = {}, secret = password) => {
    const token = await user.getIdToken()
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        'x-oracle-admin-password': secret,
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {}),
      },
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.message || 'Não foi possível carregar o CRM.')
    return data
  }, [password, user])

  const applyLead = useCallback((lead) => {
    setSelectedLead(lead)
    const manual = lead?.manualProfile || {}
    setProfileDraft({
      stage: manual.stage || 'new',
      notes: manual.notes || '',
      fields: PROFILE_FIELDS.reduce((fields, [key]) => ({ ...fields, [key]: manual.fields?.[key] || '' }), {}),
    })
  }, [])

  const loadLead = useCallback(async (leadKey, secret = password) => {
    if (!leadKey) return
    setDetailLoading(true)
    try {
      const data = await adminRequest(`/api/oracle/admin/crm/leads/${encodeURIComponent(leadKey)}`, {}, secret)
      applyLead(data.lead)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setDetailLoading(false)
    }
  }, [adminRequest, applyLead, password])

  const loadLeads = useCallback(async (query = activeLeadSearch, secret = password, selectFirst = false) => {
    const params = new URLSearchParams({ limit: '100' })
    if (query) params.set('search', query)
    const data = await adminRequest(`/api/oracle/admin/crm/leads?${params}`, {}, secret)
    setLeads(data.items || [])
    setLeadTotal(Number(data.total || 0))
    if (selectFirst && data.items?.[0]) await loadLead(data.items[0].leadKey, secret)
    return data
  }, [activeLeadSearch, adminRequest, loadLead, password])

  const loadOverview = useCallback(async (selectedPeriod = period, secret = password) => {
    const data = await adminRequest(`/api/oracle/admin/crm/overview?period=${selectedPeriod}`, {}, secret)
    setOverview(data.overview)
  }, [adminRequest, password, period])

  const loadConversations = useCallback(async ({
    source = ['live', 'polymarket', 'quantumodds'].includes(activeTab) ? activeTab : 'all',
    query = activeConversationSearch,
    offset = conversationOffset,
    secret = password,
  } = {}) => {
    const params = new URLSearchParams({ source, limit: '50', offset: String(offset) })
    if (query) params.set('search', query)
    const data = await adminRequest(`/api/oracle/admin/crm/conversations?${params}`, {}, secret)
    setConversations(data.items || [])
    setConversationTotal(Number(data.total || 0))
    setConversationOffset(Number(data.offset || 0))
  }, [activeConversationSearch, activeTab, adminRequest, conversationOffset, password])

  const unlock = useCallback(async (secret = password) => {
    if (!secret) return
    setLoading(true)
    setError('')
    try {
      await Promise.all([loadOverview('all', secret), loadLeads('', secret, true)])
      setAuthorized(true)
      sessionStorage.setItem(CRM_SESSION_KEY, secret)
    } catch (requestError) {
      setAuthorized(false)
      sessionStorage.removeItem(CRM_SESSION_KEY)
      setError(requestError.message)
    } finally {
      setLoading(false)
    }
  }, [loadLeads, loadOverview, password])

  useEffect(() => {
    if (password) unlock(password)
    // The stored CRM password is intentionally checked only on first mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const summary = overview?.summary || {}
  const selectedProfile = selectedLead?.autoProfile || {}
  const explicitFields = selectedProfile.explicit || {}
  const liveTotal = useMemo(() => leads.reduce((total, lead) => total + Number(lead.liveInteractions || 0), 0), [leads])
  const polymarketTotal = useMemo(() => leads.reduce((total, lead) => total + Number(lead.polymarketInteractions || 0), 0), [leads])
  const quantumOddsTotal = useMemo(() => leads.reduce((total, lead) => total + Number(lead.quantumOddsInteractions || 0), 0), [leads])

  const changeTab = async (tab) => {
    setActiveTab(tab)
    setError('')
    if (tab === 'conversations' || tab === 'live' || tab === 'polymarket' || tab === 'quantumodds') {
      setConversationOffset(0)
      await loadConversations({ source: ['live', 'polymarket', 'quantumodds'].includes(tab) ? tab : 'all', offset: 0 })
    }
  }

  const saveProfile = async () => {
    if (!selectedLead?.leadKey) return
    setSaving(true)
    setError('')
    try {
      await adminRequest(`/api/oracle/admin/crm/leads/${encodeURIComponent(selectedLead.leadKey)}`, {
        method: 'PATCH',
        body: JSON.stringify(profileDraft),
      })
      await Promise.all([loadLead(selectedLead.leadKey), loadLeads()])
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setSaving(false)
    }
  }

  const submitLeadSearch = async (event) => {
    event.preventDefault()
    const query = leadSearch.trim()
    setActiveLeadSearch(query)
    setSelectedLead(null)
    setLoading(true)
    try { await loadLeads(query, password, true) } catch (requestError) { setError(requestError.message) }
    finally { setLoading(false) }
  }

  const submitConversationSearch = async (event) => {
    event.preventDefault()
    const query = conversationSearch.trim()
    setActiveConversationSearch(query)
    setConversationOffset(0)
    setLoading(true)
    try { await loadConversations({ query, offset: 0 }) } catch (requestError) { setError(requestError.message) }
    finally { setLoading(false) }
  }

  const refresh = async () => {
    setLoading(true)
    setError('')
    try {
      await Promise.all([
        loadOverview(),
        loadLeads(),
        activeTab === 'leads' ? Promise.resolve() : loadConversations(),
      ])
      if (selectedLead?.leadKey) await loadLead(selectedLead.leadKey)
    } catch (requestError) { setError(requestError.message) }
    finally { setLoading(false) }
  }

  const lock = () => {
    sessionStorage.removeItem(CRM_SESSION_KEY)
    setAuthorized(false)
    setPassword('')
    setSelectedLead(null)
  }

  if (!authorized) return (
    <BaseLayout contentClassName="oracle-crm-app-container">
      <main className="oracle-crm oracle-crm-login">
        <form onSubmit={(event) => { event.preventDefault(); unlock(password.trim()) }}>
          <span>ÁREA PRIVADA</span>
          <h1>CRM dos Oráculos</h1>
          <p>Leads, perfis e todas as conversas do Oráculo e Oráculo Live.</p>
          <label htmlFor="crm-password">Palavra-passe administrativa</label>
          <input id="crm-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" autoFocus />
          <button type="submit" disabled={loading || !password.trim()}>{loading ? 'A validar…' : 'Entrar no CRM'}</button>
          {error && <div className="oracle-crm-error">{error}</div>}
        </form>
      </main>
    </BaseLayout>
  )

  return (
    <BaseLayout contentClassName="oracle-crm-app-container">
      <main className="oracle-crm">
        <header className="oracle-crm-header">
          <div><span>FILTERMARKET · PRIVATE</span><h1>CRM dos Oráculos</h1><p>Compreende cada lead através do que pergunta e da forma como interage.</p></div>
          <div className="oracle-crm-header-actions"><button onClick={refresh} disabled={loading}>{loading ? 'A atualizar…' : 'Atualizar dados'}</button><button className="is-quiet" onClick={lock}>Bloquear</button></div>
        </header>

        {error && <div className="oracle-crm-error">{error}</div>}

        <section className="oracle-crm-toolbar">
          <nav className="oracle-crm-tabs" aria-label="Secções do CRM">
            {TABS.map(([value, label]) => <button key={value} className={activeTab === value ? 'active' : ''} onClick={() => changeTab(value)}>{label}{value === 'live' && <b>{integer(liveTotal || summary.liveInteractions)}</b>}{value === 'polymarket' && <b>{integer(polymarketTotal || summary.polymarketInteractions)}</b>}{value === 'quantumodds' && <b>{integer(quantumOddsTotal || summary.quantumOddsInteractions)}</b>}</button>)}
          </nav>
          <nav className="oracle-crm-periods" aria-label="Período">
            {PERIODS.map(([value, label]) => <button key={value} className={period === value ? 'active' : ''} onClick={async () => { setPeriod(value); await loadOverview(value) }}>{label}</button>)}
          </nav>
        </section>

        <section className="oracle-crm-metrics">
          <article><span>Leads</span><strong>{integer(summary.leads)}</strong><small>emails ou contas únicas</small></article>
          <article><span>Interações</span><strong>{integer(summary.interactions)}</strong><small>{integer(summary.completed)} concluídas</small></article>
          <article><span>Oráculo</span><strong>{integer(summary.oracleInteractions)}</strong><small>perguntas gerais</small></article>
          <article><span>Oráculo Live</span><strong>{integer(summary.liveInteractions)}</strong><small>histórico em direto</small></article>
          <article><span>Quantum Odds</span><strong>{integer(summary.quantumOddsInteractions)}</strong><small>interações</small></article>
          <article><span>Tokens</span><strong>{integer(summary.totalTokens)}</strong><small>nos Oráculos</small></article>
          <article><span>Custo registado</span><strong>{money(summary.costUsd)}</strong><small>{integer(summary.failed)} falhas</small></article>
        </section>

        {activeTab === 'leads' && (
          <section className="oracle-crm-leads-layout">
            <div className="oracle-crm-panel oracle-crm-lead-list">
              <div className="oracle-crm-panel-head">
                <div><h2>Leads ({integer(leadTotal)})</h2><p>Agrupadas pelo utilizador que fez as perguntas.</p></div>
                <form onSubmit={submitLeadSearch}><input value={leadSearch} onChange={(event) => setLeadSearch(event.target.value)} placeholder="Email, desejo ou dor…" /><button>Pesquisar</button></form>
              </div>
              <div className="oracle-crm-lead-rows">
                {leads.map((lead) => (
                  <button className={selectedLead?.leadKey === lead.leadKey ? 'active' : ''} key={lead.leadKey} onClick={() => loadLead(lead.leadKey)}>
                    <div><strong>{lead.email || lead.userId || 'Sem identificação'}</strong><time>{formatDate(lead.lastInteractionAt)}</time></div>
                    <p>{lead.latestQuestion || 'Sem pergunta guardada.'}</p>
                    <footer><span>{integer(lead.interactions)} interações</span><span>{integer(lead.liveInteractions)} live</span><span className={`stage-${lead.manualProfile?.stage || 'new'}`}>{STAGES.find(([key]) => key === (lead.manualProfile?.stage || 'new'))?.[1]}</span></footer>
                  </button>
                ))}
                {!leads.length && <div className="oracle-crm-empty">Nenhuma lead corresponde à pesquisa.</div>}
              </div>
            </div>

            <div className="oracle-crm-panel oracle-crm-lead-detail">
              {detailLoading && <div className="oracle-crm-empty">A carregar o perfil…</div>}
              {!detailLoading && !selectedLead && <div className="oracle-crm-empty">Seleciona uma lead para consultar o perfil e o histórico.</div>}
              {!detailLoading && selectedLead && <>
                <div className="oracle-crm-lead-title">
                  <div><span>PERFIL DA LEAD</span><h2>{selectedLead.email || selectedLead.userId}</h2><p>{integer(selectedLead.interactions)} interações · {integer(selectedLead.conversationCount)} conversas · desde {formatDate(selectedLead.firstInteractionAt)}</p></div>
                  <select value={profileDraft.stage} onChange={(event) => setProfileDraft((current) => ({ ...current, stage: event.target.value }))}>{STAGES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
                </div>

                <div className="oracle-crm-profile-grid">
                  <section><h3>Interesses observados</h3><Tags items={selectedProfile.interests} tone="green" /></section>
                  <section><h3>Desejos e objetivos</h3><Tags items={selectedProfile.goals} tone="blue" /></section>
                  <section><h3>Dores e problemas</h3><Tags items={selectedProfile.pains} tone="orange" /></section>
                  <section><h3>Forma de comunicar</h3><Tags items={selectedProfile.mannerisms} /></section>
                  <section><h3>Casas/exchanges mencionadas</h3><Tags items={selectedProfile.mentionedPlatforms} empty="Nenhuma mencionada" /></section>
                  <section><h3>Plataformas compatíveis</h3><Tags items={selectedProfile.suggestedPlatforms} tone="purple" empty="Sem sugestão suficiente" /><small>Sugestão baseada nos mercados procurados; não significa que a lead já as utilize.</small></section>
                </div>

                <section className="oracle-crm-explicit">
                  <div><h3>Factos explicitamente identificados</h3><span>Confiança do perfil: {selectedProfile.confidence === 'high' ? 'alta' : selectedProfile.confidence === 'medium' ? 'média' : 'baixa'}</span></div>
                  <dl>
                    {PROFILE_FIELDS.slice(0, 5).map(([key, label]) => <div key={key}><dt>{label}</dt><dd>{explicitFields[key]?.value || 'Não identificado'}{explicitFields[key]?.evidence && <small>“{explicitFields[key].evidence}”</small>}</dd></div>)}
                  </dl>
                </section>

                <section className="oracle-crm-editor">
                  <div><h3>Perfil confirmado e notas</h3><p>Completa ou corrige estes dados à medida que conheces melhor a lead.</p></div>
                  <div className="oracle-crm-form-grid">
                    {PROFILE_FIELDS.map(([key, label]) => <label key={key}><span>{label}</span><textarea rows={key === 'pains' || key === 'goals' ? 3 : 2} value={profileDraft.fields[key]} onChange={(event) => setProfileDraft((current) => ({ ...current, fields: { ...current.fields, [key]: event.target.value } }))} placeholder={explicitFields[key]?.value || 'Ainda não confirmado'} /></label>)}
                    <label className="is-wide"><span>Notas internas</span><textarea rows="4" value={profileDraft.notes} onChange={(event) => setProfileDraft((current) => ({ ...current, notes: event.target.value }))} placeholder="Contexto comercial, próximos passos, observações…" /></label>
                  </div>
                  <button className="oracle-crm-save" onClick={saveProfile} disabled={saving}>{saving ? 'A guardar…' : 'Guardar perfil'}</button>
                </section>

                <section className="oracle-crm-history">
                  <div><h3>Histórico completo</h3><span>{integer(selectedLead.conversations?.length)} interações dos Oráculos</span></div>
                  {(selectedLead.conversations || []).map((turn, index) => <Conversation key={`${turn.source}:${turn._id}`} turn={turn} initiallyOpen={index === 0} />)}
                </section>
              </>}
            </div>
          </section>
        )}

        {(activeTab === 'conversations' || activeTab === 'live' || activeTab === 'polymarket' || activeTab === 'quantumodds') && (
          <section className="oracle-crm-panel oracle-crm-all-conversations">
            <div className="oracle-crm-panel-head">
              <div><h2>{activeTab === 'live' ? 'Histórico do Oráculo Live' : activeTab === 'polymarket' ? 'Histórico do Oráculo Polymarket' : activeTab === 'quantumodds' ? 'Histórico do Oráculo Quantum Odds' : 'Todas as conversas'} ({integer(conversationTotal)})</h2><p>Perguntas e respostas completas, ordenadas da mais recente para a mais antiga.</p></div>
              <form onSubmit={submitConversationSearch}><input value={conversationSearch} onChange={(event) => setConversationSearch(event.target.value)} placeholder="Pesquisar email, pergunta ou resposta…" /><button>Pesquisar</button></form>
            </div>
            <div className="oracle-crm-conversation-list">
              {conversations.map((turn, index) => <div key={`${turn.source}:${turn._id}`}><div className="oracle-crm-conversation-user">{turn.userEmail || turn.userId || 'Sem identificação'}</div><Conversation turn={turn} initiallyOpen={index === 0} /></div>)}
              {!conversations.length && <div className="oracle-crm-empty">Ainda não existem conversas nesta secção.</div>}
            </div>
            <div className="oracle-crm-pagination"><button disabled={!conversationOffset} onClick={() => loadConversations({ offset: Math.max(0, conversationOffset - 50) })}>Anterior</button><span>{conversationTotal ? `${integer(conversationOffset + 1)}–${integer(Math.min(conversationOffset + conversations.length, conversationTotal))}` : '0'} de {integer(conversationTotal)}</span><button disabled={conversationOffset + conversations.length >= conversationTotal} onClick={() => loadConversations({ offset: conversationOffset + 50 })}>Seguinte</button></div>
          </section>
        )}
      </main>
    </BaseLayout>
  )
}

export default OracleCRM
